import { Checkbox } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { XVerBaremetalPackageRender } from '../../../@types/launch';
import { Avatar, Button, NewTabLink } from '../../Atoms';

type PaymentPeriod = 'weekly' | 'monthly' | 'yearly';

interface BaremetalDeploymentSummaryCardProps {
    hostname: string;
    paymentPeriod: PaymentPeriod;
    selectedPackage?: XVerBaremetalPackageRender;
    selectedOsLabel: string | null;
    selectedOsKind: 'linux' | 'windows';
    installMinutes?: number | null;
    isDeployLoading?: boolean;
    isDeployDisabled?: boolean;
    onDeploy: () => void;
    /** Rendered above the terms checkbox, e.g. `DeployTargetNotice`. */
    notice?: ReactNode;
    /** Set when the caller may not deploy here: the button stays focusable, described by this id. */
    deployBlockedReasonId?: string;
}

const PERIOD_SUFFIX_KEY: Record<PaymentPeriod, string> = {
    weekly: 'deploymentSummaryCard.weeklySuffix',
    monthly: 'deploymentSummaryCard.monthlySuffix',
    yearly: 'deploymentSummaryCard.yearlySuffix',
};

const PERIOD_DUE_LABEL_KEY: Record<PaymentPeriod, string> = {
    weekly: 'deploymentSummaryCard.dueWeekly',
    monthly: 'deploymentSummaryCard.dueMonthly',
    yearly: 'deploymentSummaryCard.dueYearly',
};

function pickPrice(pkg: XVerBaremetalPackageRender | undefined, period: PaymentPeriod): number {
    if (!pkg) return 0;
    if (period === 'weekly') return pkg.price_weekly;
    if (period === 'monthly') return pkg.price_monthly;
    return pkg.price_yearly;
}

export default function BaremetalDeploymentSummaryCard({
    hostname,
    paymentPeriod,
    selectedPackage,
    selectedOsLabel,
    selectedOsKind,
    installMinutes,
    isDeployLoading = false,
    isDeployDisabled = false,
    onDeploy,
    notice,
    deployBlockedReasonId,
}: BaremetalDeploymentSummaryCardProps) {
    const { t } = useTranslation('components');
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);

    const priceForPeriod = pickPrice(selectedPackage, paymentPeriod);
    const priceLabel = selectedPackage ? priceForPeriod.toLocaleString() : '—';
    const periodSuffix = t(PERIOD_SUFFIX_KEY[paymentPeriod]);

    return (
        <div className="sticky top-4 self-start w-full h-auto">
            <div className="w-full h-auto rounded-md bg-surface-1 border-[0.5px] border-line overflow-hidden">
                <div
                    style={{
                        background: 'var(--summary-hero-gradient)',
                    }}
                    className="w-full h-auto p-4 flex flex-col justify-start items-start gap-3.5 border-b-[0.5px] border-line"
                >
                    <div className="inline-flex text-xs items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <span className="uppercase text-fg/80">
                            {t('deploymentSummaryCard.readyToDeploy')}
                        </span>
                    </div>
                    <div className="flex flex-row justify-start items-center gap-3">
                        <Avatar value={`${hostname}`} size={'48'} />
                        <div className="flex flex-col justify-start items-start min-w-0">
                            <span className="text-lg font-mono font-bold break-all">
                                {hostname.length === 0
                                    ? t('deploymentSummaryCard.undefined')
                                    : hostname}
                            </span>
                            <div className="inline-flex flex-wrap items-center text-sm text-fg/68 gap-x-1">
                                <span>{t('deploymentSummaryCard.region')}</span>
                                <span className="text-2xl leading-0">·</span>
                                <span>{t('deploymentSummaryCard.bareMetalCompute')}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="p-4 grid grid-cols-1 items-center gap-5">
                    <div className="flex flex-row justify-between items-start gap-3">
                        <div className="flex flex-col justify-start items-start gap-1 min-w-0 flex-1">
                            <div className="w-full font-bold text-sm text-fg break-words">
                                {selectedPackage?.cpu_label || '—'}
                            </div>
                            <div className="inline-flex items-center text-[13px] text-fg/68 gap-1 flex-wrap">
                                <span>{selectedPackage?.memory_label || '—'}</span>
                                <span className="text-xl leading-0">·</span>
                                <span>{selectedPackage?.storage_label || '—'}</span>
                                <span className="text-xl leading-0">·</span>
                                <span>{selectedPackage?.network_label || '—'}</span>
                            </div>
                        </div>
                        <div className="flex flex-row justify-end items-baseline shrink-0">
                            <span className="text-[15px] font-bold">{priceLabel}฿</span>
                            <span className="text-sm text-fg/68">{periodSuffix}</span>
                        </div>
                    </div>
                    <div className="flex flex-row justify-between items-start gap-3">
                        <div className="flex flex-col justify-start items-start gap-1 min-w-0">
                            <div className="inline-flex items-center font-bold text-sm gap-0.5 text-fg">
                                <span>{selectedOsLabel || '—'}</span>
                            </div>
                            <div className="inline-flex flex-wrap items-center text-[13px] text-fg/68 gap-x-1">
                                <span>
                                    {selectedOsKind === 'linux'
                                        ? t('deploymentSummaryCard.linuxDistribution')
                                        : t('deploymentSummaryCard.windows')}
                                </span>
                                <span className="text-xl leading-0">·</span>
                                {selectedOsKind === 'linux' ? (
                                    <span>{t('deploymentSummaryCard.openSource')}</span>
                                ) : (
                                    <span className="text-yellow-500/80 light:text-yellow-800">
                                        {t('deploymentSummaryCard.notIncludedLicense')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col justify-end items-end gap-1.5">
                            <span className="text-sm text-green-300 light:text-green-800">
                                {t('deploymentSummaryCard.included')}
                            </span>
                        </div>
                    </div>
                    {installMinutes ? (
                        <div className="inline-flex items-center gap-1.5 text-[13px] text-fg/68">
                            <IconClock size={14} aria-hidden="true" />
                            <span>
                                {t('deploymentSummaryCard.installTime', {
                                    minutes: installMinutes,
                                })}
                            </span>
                        </div>
                    ) : null}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1.5 border-t-[0.5px] border-line">
                    <div className="w-full flex flex-col justify-start items-start gap-1.5">
                        <div className="w-full flex flex-row justify-between items-center">
                            <span className="text-sm text-fg/68">
                                {t(PERIOD_DUE_LABEL_KEY[paymentPeriod])}
                            </span>
                            <span className="font-bold text-fg">{priceLabel}฿</span>
                        </div>
                    </div>

                    {notice}

                    <Checkbox
                        size="xs"
                        checked={isTermsAccepted}
                        onChange={(event) => setIsTermsAccepted(event.currentTarget.checked)}
                        label={
                            <span className="text-[13px] text-fg">
                                {t('deploymentSummaryCard.termsOfServicePrefix')}{' '}
                                <NewTabLink
                                    href={'https://xver.cloud/th/legal/terms-of-service/'}
                                    className="text-blue-400 light:text-blue-700 hover:underline"
                                >
                                    {t('deploymentSummaryCard.termsOfServiceLink')}
                                </NewTabLink>
                            </span>
                        }
                        color="blue"
                        variant="filled"
                    />

                    <Button
                        width={'100%'}
                        className="mt-1"
                        disabled={!isTermsAccepted || isDeployLoading || isDeployDisabled}
                        isLoading={isDeployLoading}
                        aria-disabled={deployBlockedReasonId ? true : undefined}
                        aria-describedby={deployBlockedReasonId}
                        style={
                            deployBlockedReasonId
                                ? { opacity: 0.5, cursor: 'not-allowed' }
                                : undefined
                        }
                        onClick={deployBlockedReasonId ? undefined : onDeploy}
                    >
                        {t('deploymentSummaryCard.deployServerPeriod', {
                            price: priceLabel,
                            suffix: periodSuffix,
                        })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
